import {getTrustTypeStrategy} from '@sollapay/domain/trust-types';
import {useEffect, useMemo, useRef} from 'react';
import {useForm, useWatch} from 'react-hook-form';

import {useUpsertScopeMutation} from '@/features/trust-accounts/activation/hooks';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';

import {flattenFieldErrors} from '../../../utils';
import {createTrustScopeFormSchema} from '../../../validation';
import {
  EMPTY_TRUST_SCOPE_FORM_VALUES,
  toTrustScopeDetailsDTO,
  toTrustScopeFormValues
} from '../utils';

import type {TrustScopeFormValues} from '../utils';
import type {FormStateSnapshot} from '@/hooks';
import type {GeneralSaleTransactionType} from '@sollapay/enums';
import type {TrustScopeDetailsDTO} from '@sollapay/types';

interface UseTrustScopeFormParams {
  trustAccountId: string;
  scope: TrustScopeDetailsDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const useTrustScopeForm = ({
  trustAccountId,
  scope,
  isLoading,
  isError,
  onFormStateChange
}: UseTrustScopeFormParams) => {
  const upsertScopeMutation = useUpsertScopeMutation(trustAccountId);
  const {trustPurpose} = useCurrentTrustAccount();

  // Shared by every call site below so they agree on one falsy-value rule ('' from the form,
  // null/undefined from a DTO) instead of each repeating `trustPurpose` and its own `?? null`.
  const resolveStrategy = (transactionType?: GeneralSaleTransactionType | '' | null) =>
    getTrustTypeStrategy({trustPurpose, generalSaleTransactionType: transactionType || null});

  const form = useForm<TrustScopeFormValues>({
    defaultValues: EMPTY_TRUST_SCOPE_FORM_VALUES
  });

  // Drives the live payout-type option list — reflects what the lawyer is choosing right now.
  const watchedTransactionType = useWatch({control: form.control, name: 'transactionType'});
  const strategy = useMemo(
    () => resolveStrategy(watchedTransactionType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trustPurpose, watchedTransactionType]
  );

  const requiresTransactionType = strategy.getRequiredScopeFields().includes('transactionType');
  const trustScopeFormSchema = useMemo(
    () => createTrustScopeFormSchema(requiresTransactionType),
    [requiresTransactionType]
  );

  // Filtered against the scope's own persisted transaction type, not the live-watched one — this
  // must stay stable while the lawyer edits, or switching the dropdown would re-fire the
  // load-reset effect below and wipe out every other unsaved field.
  const scopeDefaults = useMemo(
    () => toTrustScopeFormValues(scope, resolveStrategy(scope?.transactionType)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, trustPurpose]
  );

  useEffect(() => {
    form.reset(scopeDefaults);
  }, [form, scopeDefaults]);

  const handleSave = form.handleSubmit(async values => {
    const result = await upsertScopeMutation.mutateAsync(toTrustScopeDetailsDTO(values));
    const savedStrategy = resolveStrategy(result.data.scope.transactionType);
    form.reset(toTrustScopeFormValues(result.data.scope, savedStrategy));
  });

  const handleCancel = () => {
    form.reset(scopeDefaults);
    upsertScopeMutation.reset();
  };

  const allValues = useWatch({control: form.control, defaultValue: EMPTY_TRUST_SCOPE_FORM_VALUES});
  const validationResult = trustScopeFormSchema.safeParse(allValues);
  const hasErrors = !validationResult.success;
  const fieldErrors = flattenFieldErrors(validationResult.success ? null : validationResult.error);

  const isDirty = form.formState.isDirty;
  const isSaving = upsertScopeMutation.isPending;

  const onSaveRef = useRef(handleSave);
  onSaveRef.current = handleSave;
  const onCancelRef = useRef(handleCancel);
  onCancelRef.current = handleCancel;

  useEffect(() => {
    onFormStateChange?.({
      isDirty,
      isLoading,
      isSaving,
      isError,
      hasErrors,
      onSave: () => onSaveRef.current(),
      onCancel: () => onCancelRef.current()
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isLoading, isSaving, isError, hasErrors]);

  return {
    form,
    handleSave,
    handleCancel,
    isDirty,
    isSaving,
    isLoading,
    isError,
    saveError: upsertScopeMutation.error,
    hasErrors,
    fieldErrors,
    requiresTransactionType,
    availablePayoutTypes: strategy.getPayoutTypeOptions()
  };
};
